import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow,transform] duration-200 focus-visible:outline-none fdisabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&>div.bg-foreground]:bg-softiq-primary [&>div.bg-foreground]:text-white [&>div.bg-foreground>svg]:text-white",
  {
    variants: {
      variant: {
        default:
          "bg-softiq-primary text-white shadow hover:bg-softiq-primary-shade-600 [&>div.bg-foreground]:bg-white [&>div.bg-foreground]:text-softiq-primary [&>div.bg-foreground>svg]:text-softiq-primary",
        brand:
          "bg-softiq-secondary text-white shadow-sm hover:bg-softiq-secondary-shade-600 dark:bg-softiq-secondary dark:text-black dark:hover:bg-softiq-secondary-shade-300",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        success:
          "border border-green-700 bg-green-700 text-white shadow-sm hover:border-green-800 hover:bg-green-800 dark:border-green-600 dark:bg-green-700 dark:text-white dark:hover:border-green-500 dark:hover:bg-green-600",
        outline:
          "border border-input bg-background text-foreground shadow-sm hover:bg-softiq-secondary-shade-100/45 hover:shadow-md dark:bg-transparent dark:text-white dark:hover:bg-white/20 dark:hover:shadow-lg",
        secondary:
          "border border-border/70 bg-hover/70 text-foreground shadow-sm hover:bg-softiq-secondary-shade-100/45 hover:shadow-md data-[active=true]:border-softiq-primary data-[active=true]:bg-softiq-primary data-[active=true]:text-white data-[active=true]:shadow-xl dark:hover:bg-white/20 dark:hover:shadow-lg",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        minimal:
          "h-auto bg-transparent px-0 py-0 text-sm font-medium text-foreground hover:bg-softiq-secondary-shade-100/45",
        link: "text-primary underline-offset-4 hover:underline",
        icon: "rounded-md p-0 hover:bg-accent hover:text-accent-foreground",
        unstyled: "h-auto bg-transparent p-0 text-inherit shadow-none",
      },
      size: {
        default: "h-8 px-3 py-1.5",
        sm: "h-7 rounded-md px-2.5 text-xs",
        lg: "h-10 rounded-md px-6",
        compact: "h-9 rounded-lg px-3 text-sm",
        form: "h-10 rounded-lg px-5 text-sm",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loading?: boolean;
  loadingLabel?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      startIcon,
      endIcon,
      loading = false,
      loadingLabel,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          aria-busy={loading || undefined}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {startIcon}
        {loading ? (loadingLabel ?? children) : children}
        {endIcon}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
